import { AnalyticsBrowser } from '@segment/analytics-next';

let analyticsPromise = null;
let analyticsInstance = null;
// Version counter to invalidate in-flight requests when reset() is called
let analyticsVersion = 0;

/**
 * Initialize Segment Analytics
 * @param {string} writeKey - Segment write key from environment variables
 * @returns {Promise<Array>} Promise that resolves to [analytics, context]
 */
function initializeAnalytics(writeKey) {
  if (analyticsPromise) {
    return analyticsPromise;
  }

  if (!writeKey) {
    console.warn('[Segment Analytics] Write key not provided. Analytics will not be initialized.');
    return Promise.resolve([null, null]);
  }

  analyticsPromise = AnalyticsBrowser.load({ writeKey });
  return analyticsPromise;
}

/**
 * Get or initialize the analytics instance
 * @returns {Promise<Array>} Promise that resolves to [analytics, context]
 */
async function getAnalytics() {
  if (analyticsInstance) {
    return analyticsInstance;
  }

  // Capture current version to detect if reset() was called during initialization
  const currentVersion = analyticsVersion;
  const writeKey = process.env.REACT_APP_SEGMENT_WRITE_KEY;
  const instance = await initializeAnalytics(writeKey);
  
  // Only set instance if version hasn't changed (reset() wasn't called)
  if (currentVersion === analyticsVersion) {
    analyticsInstance = instance;
  }
  
  return instance;
}

/**
 * Track an event
 * @param {string} eventName - Name of the event
 * @param {Object} properties - Event properties
 * @param {Object} options - Additional options (userId, anonymousId, etc.)
 */
async function track(eventName, properties = {}, options = {}) {
  try {
    const [analytics] = await getAnalytics();
    if (!analytics) return;

    await analytics.track(eventName, properties, options);
  } catch (error) {
    console.error('[Segment Analytics] Error tracking event:', error);
  }
}

/**
 * Identify a user
 * @param {string} userId - User ID
 * @param {Object} traits - User traits (email, name, etc.)
 * @param {Object} options - Additional options
 */
async function identify(userId, traits = {}, options = {}) {
  try {
    const [analytics] = await getAnalytics();
    if (!analytics) return;

    await analytics.identify(userId, traits, options);
  } catch (error) {
    console.error('[Segment Analytics] Error identifying user:', error);
  }
}

/**
 * Track a page view
 * @param {string} name - Page name
 * @param {Object} properties - Page properties
 * @param {Object} options - Additional options
 */
async function page(name, properties = {}, options = {}) {
  try {
    const [analytics] = await getAnalytics();
    if (!analytics) return;

    await analytics.page(name, properties, options);
  } catch (error) {
    console.error('[Segment Analytics] Error tracking page:', error);
  }
}

/**
 * Reset user identity (on logout)
 */
async function reset() {
  try {
    const [analytics] = await getAnalytics();
    if (!analytics) return;

    // Increment version FIRST to invalidate any in-flight initialization requests
    // This ensures concurrent getAnalytics() calls will detect the version change
    // and won't cache stale instances
    analyticsVersion++;
    
    // Clear cached instances BEFORE reset to prevent returning stale instances
    // to concurrent getAnalytics() calls
    analyticsInstance = null;
    analyticsPromise = null;

    // Reset the analytics instance (we still have the reference in local variable)
    await analytics.reset();
  } catch (error) {
    console.error('[Segment Analytics] Error resetting user:', error);
  }
}

/**
 * Set user traits
 * @param {Object} traits - User traits to set
 */
async function setTraits(traits) {
  try {
    const [analytics] = await getAnalytics();
    if (!analytics) return;

    await analytics.setTraits(traits);
  } catch (error) {
    console.error('[Segment Analytics] Error setting traits:', error);
  }
}

// Initialize on import if write key is available
if (process.env.REACT_APP_SEGMENT_WRITE_KEY) {
  initializeAnalytics(process.env.REACT_APP_SEGMENT_WRITE_KEY);
}

const analytics = {
  track,
  identify,
  page,
  reset,
  setTraits,
  initialize: initializeAnalytics,
};

export default analytics;
